import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Mail,
  MapPin,
  Building2,
  Send,
} from "lucide-react";
import { LogoLockup } from "./Logo";

/**
 * Editorial landing-page footer.
 *
 * Dark indigo plate with a hairline terracotta rule, four content columns
 * (brand · platform · roles · partners) and a slim bottom strip.
 *
 * Composition rules:
 *   • Asymmetric grid — the brand column is wider than the link columns.
 *   • Fraunces for column titles, Newsreader italic for accents, Nunito Sans
 *     for nav and metadata.
 *   • Hairline dividers (#FBFAFD @ 0.08–0.16 opacity), no glassmorphism.
 *   • Every clickable element has a data-testid for the testing agent.
 *
 * The newsletter row is a courtesy mailto handoff (no backend yet) — the
 * email value is captured, validated client-side, and a `mailto:` link is
 * synthesised so the user's mail client takes over.
 */
export default function LandingFooter() {
  return (
    <footer
      data-testid="landing-footer"
      style={{
        backgroundColor: "#13163C",
        color: "#FBFAFD",
        marginTop: 96,
      }}
    >
      {/* Top terracotta hairline */}
      <div
        aria-hidden
        style={{ height: 2, background: "#B14E2F", width: "100%" }}
      />

      <div className="max-w-[1280px] mx-auto px-6 lg:px-16 xl:px-24 pt-16 pb-10">
        <div
          className="grid gap-x-10 gap-y-12"
          style={{
            gridTemplateColumns: "minmax(0, 1.6fr) repeat(3, minmax(0, 1fr))",
          }}
        >
          <BrandColumn />
          <LinkColumn
            title="Explore."
            testId="footer-col-explore"
            items={[
              { to: "/marketplace", label: "Design marketplace", testid: "footer-link-marketplace" },
              { to: "/parcels", label: "Vacant parcels", testid: "footer-link-parcels" },
              { to: "/architects", label: "Architect directory", testid: "footer-link-architects" },
              { to: "/registry", label: "Project registry", testid: "footer-link-registry" },
              { to: "/resources", label: "Resources", testid: "footer-link-resources" },
            ]}
          />
          <LinkColumn
            title="Join."
            testId="footer-col-join"
            items={[
              { to: "/sign-in", label: "Sign in", testid: "footer-link-signin" },
              { to: "/register/architect", label: "Apply as an architect", testid: "footer-link-apply" },
              {
                href: "mailto:partnerships@wholeneighborhoods.org",
                label: "Partner with us",
                testid: "footer-link-partners",
              },
              {
                href: "mailto:demo@wholeneighborhoods.org",
                label: "Request a demo",
                testid: "footer-link-demo",
              },
            ]}
          />
          <PartnersColumn />
        </div>

        <NewsletterRow />

        <div
          style={{
            borderTop: "1px solid rgba(251,250,253,0.12)",
            marginTop: 40,
            paddingTop: 22,
          }}
          className="flex flex-wrap items-center justify-between gap-4"
        >
          <div
            className="tabular-nums"
            style={{
              fontSize: 12,
              color: "rgba(251,250,253,0.62)",
              letterSpacing: "0.04em",
            }}
            data-testid="footer-copyright"
          >
            © 2026 WHOLE Neighborhoods · Proof of concept · Indianapolis
          </div>
          <div
            className="wn-label"
            style={{
              color: "rgba(251,250,253,0.5)",
              letterSpacing: "0.12em",
              fontSize: 10,
            }}
            data-testid="footer-volume"
          >
            Volume 01 — Field study in progress
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Brand column                                                            */
/* ─────────────────────────────────────────────────────────────────────── */

function BrandColumn() {
  return (
    <div data-testid="footer-col-brand">
      <LogoLockup tone="white" size={32} />
      <p
        className="mt-5"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "rgba(251,250,253,0.78)",
          fontSize: 15,
          lineHeight: 1.6,
          maxWidth: 380,
        }}
        data-testid="footer-mission"
      >
        Pre-reviewed homes, matched to real parcels — written, block by
        block, with the City of Indianapolis and the architects who serve
        it.
      </p>
      <div
        className="mt-6 flex flex-col gap-3"
        style={{ fontSize: 13, color: "rgba(251,250,253,0.78)" }}
      >
        <div className="flex items-start gap-2" data-testid="footer-address">
          <MapPin
            size={14}
            style={{ marginTop: 3, color: "#B14E2F", flexShrink: 0 }}
          />
          <span style={{ lineHeight: 1.55 }}>
            200 East Washington Street
            <br />
            Indianapolis, IN 46204
          </span>
        </div>
        <a
          href="mailto:hello@wholeneighborhoods.org"
          className="flex items-center gap-2"
          style={{
            color: "rgba(251,250,253,0.78)",
            textDecoration: "none",
            fontSize: 13,
            transition: "color 160ms ease",
          }}
          data-testid="footer-email"
          onMouseEnter={(e) => (e.currentTarget.style.color = "#FBFAFD")}
          onMouseLeave={(e) =>
            (e.currentTarget.style.color = "rgba(251,250,253,0.78)")
          }
        >
          <Mail size={14} style={{ color: "#B14E2F" }} />
          hello@wholeneighborhoods.org
        </a>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Link column                                                             */
/* ─────────────────────────────────────────────────────────────────────── */

function LinkColumn({ title, items, testId }) {
  return (
    <div data-testid={testId}>
      <h4
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 16,
          fontWeight: 600,
          color: "#FBFAFD",
          marginBottom: 18,
          letterSpacing: "-0.005em",
        }}
      >
        {title}
      </h4>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {items.map((it) => (
          <li key={it.label} style={{ marginBottom: 11 }}>
            {it.to ? (
              <Link
                to={it.to}
                data-testid={it.testid}
                style={linkStyle}
                onMouseEnter={(e) => onHover(e, true)}
                onMouseLeave={(e) => onHover(e, false)}
              >
                {it.label}
              </Link>
            ) : (
              <a
                href={it.href}
                data-testid={it.testid}
                style={linkStyle}
                onMouseEnter={(e) => onHover(e, true)}
                onMouseLeave={(e) => onHover(e, false)}
              >
                {it.label}
                <ArrowUpRight size={11} style={{ opacity: 0.6 }} />
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

const linkStyle = {
  color: "rgba(251,250,253,0.78)",
  fontSize: 13.5,
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  transition: "color 160ms ease, transform 160ms ease",
};

function onHover(e, on) {
  e.currentTarget.style.color = on ? "#FBFAFD" : "rgba(251,250,253,0.78)";
  e.currentTarget.style.transform = on ? "translateX(3px)" : "translateX(0)";
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Partners column                                                         */
/* ─────────────────────────────────────────────────────────────────────── */

function PartnersColumn() {
  const partners = [
    "City of Indianapolis",
    "Department of Metropolitan Development",
    "LISC Indianapolis",
    "Indy Chamber",
  ];
  return (
    <div data-testid="footer-col-partners">
      <h4
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 16,
          fontWeight: 600,
          color: "#FBFAFD",
          marginBottom: 18,
          letterSpacing: "-0.005em",
        }}
      >
        Civic partners.
      </h4>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {partners.map((p) => (
          <li
            key={p}
            className="flex items-start gap-2"
            style={{
              marginBottom: 10,
              fontSize: 13,
              color: "rgba(251,250,253,0.78)",
              lineHeight: 1.5,
            }}
          >
            <Building2
              size={13}
              style={{ marginTop: 3, color: "#B14E2F", flexShrink: 0 }}
              aria-hidden
            />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <div
        style={{
          marginTop: 16,
          padding: "10px 12px",
          border: "1px solid rgba(251,250,253,0.18)",
          borderRadius: 4,
          background: "rgba(251,250,253,0.03)",
        }}
      >
        <div className="wn-label" style={{ color: "#B14E2F", fontSize: 10 }}>
          Volume 01 · 2026
        </div>
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            fontSize: 12.5,
            color: "rgba(251,250,253,0.72)",
            lineHeight: 1.5,
            marginTop: 4,
          }}
        >
          A field study in civic infill — written parcel by parcel.
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Newsletter (mailto handoff)                                             */
/* ─────────────────────────────────────────────────────────────────────── */

function NewsletterRow() {
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const showError = touched && email && !valid;
  const mailto = valid
    ? `mailto:hello@wholeneighborhoods.org?subject=${encodeURIComponent(
        "Subscribe to the WHOLE field study",
      )}&body=${encodeURIComponent(
        `Please add ${email} to the WHOLE Neighborhoods updates list.`,
      )}`
    : "#";

  return (
    <div
      style={{
        marginTop: 56,
        paddingTop: 32,
        borderTop: "1px solid rgba(251,250,253,0.12)",
      }}
      className="grid gap-6 lg:grid-cols-[1.2fr_1fr] items-center"
      data-testid="footer-newsletter"
    >
      <div>
        <h4
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: 22,
            fontWeight: 600,
            color: "#FBFAFD",
            lineHeight: 1.2,
          }}
        >
          Follow the field study.
        </h4>
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "rgba(251,250,253,0.72)",
            fontSize: 14,
            marginTop: 6,
            lineHeight: 1.55,
            maxWidth: 480,
          }}
        >
          Quarterly notes on parcel activations, new approved designs, and
          the architects shaping the next Indianapolis block.
        </p>
      </div>
      <form
        className="flex flex-wrap items-stretch gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (valid) {
            window.location.href = mailto;
          }
        }}
        data-testid="footer-newsletter-form"
      >
        <input
          type="email"
          placeholder="your@firm.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setTouched(true)}
          style={{
            flex: 1,
            minWidth: 220,
            background: "rgba(251,250,253,0.06)",
            border: `1px solid ${
              showError ? "rgba(177,78,47,0.6)" : "rgba(251,250,253,0.22)"
            }`,
            color: "#FBFAFD",
            padding: "12px 14px",
            fontSize: 14,
            borderRadius: 4,
            fontFamily: '"Nunito Sans", sans-serif',
            outline: "none",
          }}
          data-testid="footer-newsletter-input"
          aria-label="Email address"
          aria-invalid={showError ? "true" : "false"}
        />
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2"
          style={{
            background: "#B14E2F",
            color: "#FBFAFD",
            border: "1px solid #B14E2F",
            padding: "12px 22px",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            borderRadius: 4,
            cursor: "pointer",
            transition: "background-color 160ms ease",
          }}
          data-testid="footer-newsletter-submit"
          onMouseEnter={(e) => (e.currentTarget.style.background = "#8F3C23")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#B14E2F")}
        >
          Subscribe <Send size={13} />
        </button>
      </form>
      {showError && (
        <div
          style={{
            gridColumn: "1 / -1",
            color: "#E89B82",
            fontSize: 12,
            marginTop: -4,
          }}
          data-testid="footer-newsletter-error"
        >
          Enter a valid email address.
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* end                                                                     */
/* ─────────────────────────────────────────────────────────────────────── */
